import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Receipt, MessageCircle, Calendar } from "lucide-react";
import { Header } from "../components/Header";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { CarteraSummaryCard, STATUS_CONFIG } from "../components/CarteraSummaryCard";
import { useSession } from "../context/SessionContext";
import { clientApi, formatCOP } from "../lib/api";
import { openWhatsApp, whatsappUrl } from "../lib/whatsapp";

const ESTADO_FACTURA = {
  al_dia: { label: "Al día", className: "bg-green-100 text-green-800 border-green-300" },
  por_vencer: { label: "Por vencer", className: "bg-amber-100 text-amber-800 border-amber-300" },
  vencida: { label: "Vencida", className: "bg-red-100 text-red-800 border-red-300" },
};

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

export default function MyCartera() {
  const { customer } = useSession();
  const [cartera, setCartera] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    clientApi.get("/portal/cartera")
      .then(({ data }) => setCartera(data || null))
      .catch(() => setCartera(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-cm-cream" data-testid="my-cartera-page">
      <Header />
      <main className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <Link
              to="/catalogo"
              className="inline-flex items-center text-xs text-cm-ink-soft hover:text-cm-ink transition-colors -ml-1"
              data-testid="cartera-back-link"
            >
              <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Volver al catálogo
            </Link>
            <p className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium mt-2">Estado financiero</p>
            <h1 className="font-serif text-3xl mt-1 tracking-tight">Mi cartera</h1>
            <p className="text-sm text-cm-ink-soft mt-1 max-w-2xl">
              Resumen de tu cupo de crédito, facturas pendientes y estado con CM Calidad y Servicio.
              Actualizado diariamente desde el ERP.
            </p>
          </div>
        </div>

        {loading ? (
          <Card className="p-12 text-center bg-white border-cm-border shadow-none">
            <Loader2 className="h-8 w-8 mx-auto animate-spin text-cm-ink-soft" />
            <p className="text-sm text-cm-ink-soft mt-3">Cargando cartera...</p>
          </Card>
        ) : !cartera ? (
          <Card className="p-12 text-center bg-white border-cm-border shadow-none">
            <Receipt className="h-12 w-12 mx-auto text-cm-ink-soft/40" />
            <h2 className="font-serif text-xl mt-4">Sin información de cartera</h2>
            <p className="text-sm text-cm-ink-soft mt-2">Vuelve a intentarlo en unos segundos o contacta a soporte.</p>
          </Card>
        ) : (
          <>
            <CarteraSummaryCard cartera={cartera} customer={customer} />

            {/* Facturas detail — only shown when we have real data from Arcos */}
            {cartera.n_facturas > 0 ? (
              <Card className="bg-white border-cm-border shadow-none overflow-hidden" data-testid="cartera-facturas-card">
              <div className="px-5 py-4 border-b border-cm-border flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h2 className="font-serif text-lg">Facturas pendientes</h2>
                  <p className="text-xs text-cm-ink-soft mt-0.5">
                    {cartera.n_facturas === 0
                      ? "No tienes facturas pendientes."
                      : `${cartera.n_facturas} factura${cartera.n_facturas === 1 ? "" : "s"} con saldo · Plazo comercial: ${cartera.plazo_dias} días`}
                  </p>
                </div>
                {cartera.updated_at && (
                  <p className="text-[11px] text-cm-ink-soft font-mono flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Actualizado: {new Date(cartera.updated_at).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" })}
                  </p>
                )}
              </div>
              {cartera.facturas?.length > 0 ? (
                <div className="overflow-x-auto">
                  <div className="px-5 py-3 bg-cm-cream-alt/60 border-b border-cm-border flex items-start gap-2" data-testid="cartera-origen-info">
                    <Receipt className="h-4 w-4 text-cm-ink-soft flex-shrink-0 mt-0.5" />
                    <p className="text-[11px] text-cm-ink-soft leading-relaxed">
                      <strong className="text-cm-ink">Origen</strong> muestra de dónde salió cada cobro:{" "}
                      <span className="inline-flex items-center gap-1 mx-0.5 text-[10px] px-1.5 py-0.5 rounded-full border border-blue-300 bg-blue-50 text-blue-800 font-medium align-middle">Portal B2B</span> es un pedido que hiciste en el portal;{" "}
                      <span className="inline-flex items-center gap-1 mx-0.5 text-[10px] px-1.5 py-0.5 rounded-full border border-cm-border bg-white text-cm-ink-soft align-middle">Presencial</span> lo tomó tu vendedor en ruta.
                      Ambos se cobran igual — solo cambia el canal por el que se registró.
                    </p>
                  </div>
                  {cartera.total_notas_credito > 0 && (
                    <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-center gap-3" data-testid="cartera-nc-banner">
                      <Receipt className="h-4 w-4 text-amber-700 flex-shrink-0" />
                      <p className="text-xs text-amber-900">
                        Tienes <strong>{cartera.n_con_nc} factura{cartera.n_con_nc === 1 ? "" : "s"}</strong> con notas crédito aplicadas por un total
                        de <strong className="font-mono">{formatCOP(cartera.total_notas_credito)}</strong>. El <strong>saldo real</strong> ya descuenta esas notas — es lo que efectivamente debés pagar.
                      </p>
                    </div>
                  )}
                  <table className="w-full text-sm">
                    <thead className="text-left text-[10px] uppercase tracking-widest text-cm-ink-soft bg-cm-cream-alt/40">
                      <tr>
                        <th className="px-5 py-2.5">Factura</th>
                        <th className="px-2 py-2.5">Fecha</th>
                        <th className="px-2 py-2.5">Vence</th>
                        <th className="px-2 py-2.5">Origen</th>
                        <th className="px-2 py-2.5">Estado</th>
                        <th className="px-2 py-2.5 text-right">Vr. a pagar</th>
                        <th className="px-2 py-2.5 text-right">Nota crédito</th>
                        <th className="px-5 py-2.5 text-right">Saldo real</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-cm-border">
                      {cartera.facturas.map((f, idx) => {
                        const est = ESTADO_FACTURA[f.estado_factura] || ESTADO_FACTURA.al_dia;
                        const vnc = Number(f.valor_nota_credito || 0);
                        const origen = f.origen || null;
                        const isPortal = origen && origen.tipo === "portal";
                        return (
                          <tr key={f.numero || idx} className="hover:bg-cm-cream-alt/30" data-testid={`factura-row-${idx + 1}`}>
                            <td className="px-5 py-3 font-mono text-cm-red font-medium">{f.numero}</td>
                            <td className="px-2 py-3 text-xs text-cm-ink-soft">{fmt(f.fecha)}</td>
                            <td className="px-2 py-3 text-xs">{fmt(f.fecha_vencimiento)}</td>
                            <td className="px-2 py-3" data-testid={`factura-origen-${idx + 1}`}>
                              {isPortal ? (
                                <Link
                                  to="/mis-pedidos"
                                  className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100 transition-colors"
                                  title={`Este cobro salió del pedido ${origen.order_number}. Click para verlo.`}
                                >
                                  Portal B2B
                                </Link>
                              ) : (
                                <span
                                  className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-cm-border bg-white text-cm-ink-soft"
                                  title="Pedido tomado por el vendedor en ruta"
                                >
                                  Presencial
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-3">
                              <Badge variant="outline" className={`text-[10px] ${est.className}`}>
                                {est.label}
                                {f.dias_vencida > 0 && ` · ${f.dias_vencida}d`}
                              </Badge>
                            </td>
                            <td className="px-2 py-3 text-right font-mono text-xs text-cm-ink-soft">{formatCOP(f.total)}</td>
                            <td className="px-2 py-3 text-right font-mono text-xs">
                              {vnc > 0 ? (
                                <span className="text-amber-700 font-semibold" data-testid={`factura-nc-${idx + 1}`}>
                                  −{formatCOP(vnc)}
                                </span>
                              ) : (
                                <span className="text-cm-ink-soft/40">—</span>
                              )}
                            </td>
                            <td className="px-5 py-3 text-right font-mono font-semibold">{formatCOP(f.saldo)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-sm text-cm-ink-soft" data-testid="cartera-no-facturas">
                  Estás al día con CM Calidad y Servicio · No tienes facturas pendientes.
                </div>
              )}
            </Card>
            ) : (
              <Card className="bg-white border-cm-border shadow-none p-6" data-testid="cartera-info-card">
                <h2 className="font-serif text-lg mb-2">Información de tu cupo de crédito</h2>
                <p className="text-sm text-cm-ink-soft">
                  Para consultar el detalle de tus facturas pendientes, saldo actual o realizar pagos,
                  contacta directamente a nuestro equipo de cartera.
                </p>
              </Card>
            )}

            {/* Help */}
            <Card className="bg-cm-ink text-cm-cream border-0 shadow-none p-5" data-testid="cartera-help-card">
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <p className="text-[10px] uppercase tracking-widest text-cm-yellow font-medium">¿Tienes dudas?</p>
                  <h3 className="font-serif text-lg mt-1">Equipo de cartera</h3>
                  <p className="text-sm text-cm-cream/70 mt-1">
                    Contacta a nuestro equipo para consultar saldo, abonar o gestionar pagos.
                  </p>
                </div>
                <a
                  href={whatsappUrl("573045729333", `Hola, soy ${customer?.razon_social || customer?.almacen || "cliente"} (NIT ${customer?.nit}) y quiero consultar mi estado de cartera.`)}
                  onClick={(e) => {
                    e.preventDefault();
                    openWhatsApp("573045729333", `Hola, soy ${customer?.razon_social || customer?.almacen || "cliente"} (NIT ${customer?.nit}) y quiero consultar mi estado de cartera.`);
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded font-medium text-sm hover:bg-green-700 transition-colors"
                  data-testid="cartera-help-whatsapp"
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp 304 5729 333
                </a>
              </div>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
